import { useState } from 'react';
import {
  Check,
  Download,
  ExternalLink,
  FileText,
  MapPin,
  Printer,
  UserCog,
  X,
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { StatusPill } from '@/components/ui/StatusPill';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Avatar } from '@/components/ui/Avatar';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import {
  useRequestDetail,
  useConversationForRequest,
  useConversationMessages,
  useSendMessage,
} from '@/data/api';
import { formatDateTime, formatKes, formatRelative } from '@/lib/format';
import {
  REQUEST_TYPE_LABEL,
  type CustomerRequest,
  type RequestDocument,
} from '@/types/request';
import { ChatComposer, MessageBubble } from '@/features/messages/Chat';
import { cn } from '@/lib/cn';

export function RequestDetailDrawer({
  id,
  onClose,
  onApprove,
  onReject,
}: {
  id: string | null;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}) {
  const detailQuery = useRequestDetail(id);
  const r = detailQuery.data ?? null;
  const [tab, setTab] = useState('overview');

  // Print slip: force the Overview tab (the only thing the slip cares
  // about), give React a microtask to flush the DOM change, then trigger
  // the browser's print dialog. The CSS in styles/index.css restricts
  // what actually appears on the printed page.
  function handlePrint() {
    setTab('overview');
    setTimeout(() => window.print(), 50);
  }

  return (
    <Drawer
      open={!!r}
      onOpenChange={(o) => !o && onClose()}
      header={
        r ? (
          <div className="flex items-center gap-3">
            <div>
              <div className="font-mono text-h2 text-navy">{r.referenceNumber}</div>
              <div className="text-caption text-ink-muted">
                {REQUEST_TYPE_LABEL[r.requestType]} · Submitted {formatRelative(r.submittedAt)}
              </div>
            </div>
            <StatusPill status={r.status} />
          </div>
        ) : null
      }
      footer={
        r ? (
          <>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" iconLeft={<UserCog className="h-3.5 w-3.5" />}>
                Reassign
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrint}
                iconLeft={<Printer className="h-3.5 w-3.5" />}
              >
                Print slip
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="danger"
                onClick={() => onReject(r.id)}
                iconLeft={<X className="h-4 w-4" />}
              >
                Reject
              </Button>
              <Button
                variant="success"
                onClick={() => onApprove(r.id)}
                iconLeft={<Check className="h-4 w-4" />}
              >
                Approve
              </Button>
            </div>
          </>
        ) : null
      }
    >
      {r && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="sticky top-0 bg-white z-10">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="customer">Customer</TabsTrigger>
            <TabsTrigger value="chat">Chat</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab r={r} />
          </TabsContent>

          <TabsContent value="activity">
            <ul className="px-6 py-4 space-y-4">
              {(r.activity ?? []).map((a) => (
                <li key={a.id} className="flex gap-3">
                  <div className="mt-0.5">
                    <Avatar name={a.actorName} size="sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-body text-ink">
                      <span className="font-semibold">{a.actorName}</span>{' '}
                      <span className="text-ink-muted">{a.detail}</span>
                    </div>
                    <div className="text-caption text-ink-muted">
                      {formatDateTime(a.createdAt)}
                    </div>
                  </div>
                </li>
              ))}
              {(r.activity ?? []).length === 0 && (
                <EmptyState
                  title="No activity yet"
                  description="Status changes and notes will appear here."
                />
              )}
            </ul>
          </TabsContent>

          <TabsContent value="customer">
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Avatar name={r.contactName} size="lg" />
                <div>
                  <div className="text-h3 text-ink">{r.contactName}</div>
                  <div className="text-caption text-ink-muted">{r.contactMobile}</div>
                </div>
              </div>
              <div className="rounded-lg border border-line divide-y divide-line-soft">
                <KVRow label="Mobile" value={r.contactMobile} />
                <KVRow label="Email" value={r.contactEmail ?? '—'} />
                <KVRow label="Linked account" value={r.accountNumber ?? '—'} mono />
                <KVRow label="Service area" value={r.location ?? '—'} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="chat">
            <ChatTab r={r} />
          </TabsContent>
        </Tabs>
      )}
    </Drawer>
  );
}

function OverviewTab({ r }: { r: CustomerRequest }) {
  const hasReading =
    r.readingMeterNumber || r.readingCurrent != null || r.readingPrevious != null;
  const documents = r.documents ?? [];
  return (
    <div id="print-target" className="p-6 space-y-5">
      {/* Print-only header — visible only on the printed page. */}
      <div className="hidden print:block print:mb-6">
        <div className="text-h1 text-ink font-bold">{r.referenceNumber}</div>
        <div className="text-body text-ink-muted">
          {REQUEST_TYPE_LABEL[r.requestType]} · {r.status.replace('_', ' ')}
        </div>
      </div>
      <KV label="Account" value={r.accountNumber ?? '—'} mono />
      <KV label="Account name" value={r.accountName ?? '—'} />
      <KV label="Customer" value={r.contactName} />
      <KV label="Phone" value={r.contactMobile} mono />
      {r.contactEmail && <KV label="Email" value={r.contactEmail} />}
      <KV
        label="Location"
        value={r.location ?? '—'}
        icon={<MapPin className="h-3.5 w-3.5" />}
      />
      {r.totalAmount != null && r.totalAmount > 0 && (
        <KV label="Amount" value={`KES ${formatKes(r.totalAmount)}`} mono />
      )}
      {r.quantityValue != null && (
        <KV
          label="Quantity"
          value={`${r.quantityValue} ${r.quantityUnit ?? ''}`.trim()}
        />
      )}
      {r.reason && <KV label="Type of issue" value={r.reason} />}
      {r.description && (
        <div>
          <div className="text-caption text-ink-muted font-medium mb-1">Description</div>
          <p className="text-body text-ink leading-relaxed">{r.description}</p>
        </div>
      )}

      {hasReading && (
        <div>
          <div className="text-caption text-ink-muted font-medium mb-2">Meter reading</div>
          <div className="rounded-lg border border-line divide-y divide-line-soft">
            <KVRow label="Meter number" value={r.readingMeterNumber ?? '—'} mono />
            <KVRow
              label="Previous reading"
              value={
                r.readingPrevious != null
                  ? `${formatKes(r.readingPrevious, 0)} m³`
                  : '—'
              }
              mono
            />
            <KVRow
              label="Current reading"
              value={
                r.readingCurrent != null
                  ? `${formatKes(r.readingCurrent, 0)} m³`
                  : '—'
              }
              mono
            />
            <KVRow
              label="Consumption"
              value={
                r.readingUnits != null
                  ? `${formatKes(r.readingUnits, 0)} units`
                  : '—'
              }
              mono
            />
            {r.readingType && <KVRow label="Reading type" value={r.readingType} />}
          </div>
        </div>
      )}

      {r.photoUrls && r.photoUrls.length > 0 && (
        <div>
          <div className="text-caption text-ink-muted font-medium mb-2">Photos</div>
          <div className="flex gap-2 overflow-x-auto scrollbar-thin">
            {r.photoUrls.map((url, i) => (
              <PhotoThumb key={i} url={url} alt="Photo" />
            ))}
          </div>
        </div>
      )}

      {documents.length > 0 && (
        <div>
          <div className="text-caption text-ink-muted font-medium mb-2">Documents</div>
          <div className="flex flex-wrap gap-2">
            {documents.map((d) => (
              <DocumentTile key={d.kind} doc={d} />
            ))}
          </div>
        </div>
      )}

      {r.latitude != null && r.longitude != null && (
        <div>
          <div className="text-caption text-ink-muted font-medium mb-2">Map</div>
          <iframe
            title="Map"
            className="w-full h-[220px] rounded-lg border border-line"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${r.longitude - 0.005},${r.latitude - 0.005},${r.longitude + 0.005},${r.latitude + 0.005}&layer=mapnik&marker=${r.latitude},${r.longitude}`}
          />
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${r.latitude},${r.longitude}&travelmode=driving`}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-caption font-semibold text-brand-blue hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Get directions on Google Maps
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * Thumbnail that opens the photo full-screen in a Radix dialog when
 * clicked. Field-team photos are often blurry or shot from awkward
 * angles — staff need to zoom in to read the meter dial.
 */
function PhotoThumb({ url, alt }: { url: string; alt: string }) {
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="h-32 w-44 shrink-0 rounded-lg overflow-hidden border border-line hover:border-brand-blue transition-colors focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
          aria-label={`Open ${alt}`}
        >
          <img src={url} alt={alt} className="h-full w-full object-cover" />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/80 animate-fade-in" />
        <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-6 animate-scale-in">
          <Dialog.Title className="sr-only">{alt}</Dialog.Title>
          <img
            src={url}
            alt={alt}
            className="max-h-full max-w-full rounded-lg shadow-modal object-contain"
          />
          <Dialog.Close
            className="absolute top-4 right-4 rounded-full bg-white/90 p-2 hover:bg-white"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-ink" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * One document tile. Images render with a thumbnail preview that opens
 * the same fullscreen lightbox photos use, so the staff can zoom in on a
 * blurry ID photo. PDFs and other files render as a download chip —
 * `download` attribute on the anchor preserves the original filename.
 */
function DocumentTile({ doc }: { doc: RequestDocument }) {
  if (doc.isImage) {
    return (
      <div className="flex flex-col gap-1 shrink-0">
        <PhotoThumb url={doc.dataUrl} alt={doc.label} />
        <a
          href={doc.dataUrl}
          download={doc.filename}
          className="text-caption font-semibold text-brand-blue hover:underline truncate max-w-[176px]"
          title={doc.filename}
        >
          {doc.label}
        </a>
      </div>
    );
  }
  return (
    <a
      href={doc.dataUrl}
      download={doc.filename}
      className="group inline-flex items-center gap-2 rounded-lg border border-line bg-white px-3 py-2 hover:border-brand-blue hover:bg-canvas transition-colors"
      title={doc.filename}
    >
      <span className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-status-info-bg text-brand-blue shrink-0">
        <FileText className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <div className="text-body-strong text-ink truncate max-w-[180px]">
          {doc.label}
        </div>
        <div className="text-caption text-ink-muted truncate max-w-[180px]">
          {doc.filename}
        </div>
      </div>
      <Download className="h-4 w-4 text-ink-muted group-hover:text-brand-blue shrink-0" />
    </a>
  );
}

function ChatTab({ r }: { r: CustomerRequest }) {
  const convQuery = useConversationForRequest(
    r.userId
      ? {
          requestId: r.id,
          userId: r.userId,
          customerName: r.contactName,
          customerPhone: r.contactMobile,
          accountNumber: r.accountNumber,
          requestReference: r.referenceNumber,
        }
      : null,
  );
  const conv = convQuery.data;
  const messagesQuery = useConversationMessages(conv?.id ?? null);
  const send = useSendMessage();

  if (!r.userId) {
    return (
      <div className="p-6">
        <EmptyState
          title="No app account linked"
          description="This request came in through the web form, not the mobile app, so there's no customer to chat with yet."
        />
      </div>
    );
  }
  if (convQuery.isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-10 w-2/3" />
      </div>
    );
  }
  if (convQuery.isError || !conv) {
    return (
      <div className="p-6">
        <EmptyState
          title="Couldn't load chat"
          description={(convQuery.error as Error)?.message ?? 'Try again later.'}
        />
      </div>
    );
  }

  const messages = messagesQuery.data ?? [];

  return (
    <div className="flex flex-col h-[calc(100vh-200px)]">
      <div className={cn('flex-1 overflow-y-auto px-6 py-4 space-y-3 scrollbar-thin bg-canvas')}>
        {messages.length === 0 ? (
          <div className="text-center text-caption text-ink-muted py-8">
            No messages yet. Be the first to write to {r.contactName.split(' ')[0]}.
          </div>
        ) : (
          messages.map((m) => <MessageBubble key={m.id} message={m} />)
        )}
      </div>
      <div className="border-t border-line">
        <ChatComposer
          onSend={(body, internal) =>
            send.mutate({ conversationId: conv.id, body, internal })
          }
        />
      </div>
    </div>
  );
}

function KV({
  label,
  value,
  mono = false,
  icon,
}: {
  label: string;
  value: string;
  mono?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-baseline">
      <div className="text-caption text-ink-muted">{label}</div>
      <div className={`flex items-center gap-1.5 text-body ${mono ? 'font-mono' : ''} text-ink`}>
        {icon && <span className="text-ink-muted">{icon}</span>}
        <span>{value}</span>
      </div>
    </div>
  );
}

function KVRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <div className="text-caption text-ink-muted">{label}</div>
      <div className={`text-body ${mono ? 'font-mono' : ''} text-ink`}>{value}</div>
    </div>
  );
}
